// @flow
import React, { Component } from 'react';
import { FormattedMessage } from 'react-intl';
import type { IntlShape } from 'react-intl';
import { connect } from 'react-redux';
import { Redirect, Route, Switch, withRouter } from 'react-router-dom';
import type { Location, RouterHistory } from 'react-router-dom';

import type { Catalog, Dispatch } from 'state/types';
import { bindActionCreatorsSafe } from 'util/safeDispatch';
import { CatalogNavLink, CatalogRoute } from './CatalogRoutes';
import { getActivePanelId, getCatalogs, getIsFullAdmin } from 'state/selectors';
import {
  createCatalog,
  deleteCatalog,
  getCatalogs as getCatalogsAction,
  getCatalogTypes as getCatalogTypesAction,
} from 'state/commands/catalogCommands';
import { showDialogPanel } from 'state/commands/dialogPanelCommands';
import localizedStrings from 'util/localizedStrings';
import CreateCatalogDialog from './CreateCatalogDialog';
import DeleteVaultCatalogDialog from './DeleteVaultCatalogDialog';
import {
  CREATE_CATALOG_PANEL_ID,
  DELETE_VAULT_CATALOG_PANEL_ID,
} from 'util/constants';
import { catalogPages } from 'view/App/pageData';

type Props = {
  activePanelId: ?string,
  catalogs: Catalog[],
  createCatalog: typeof createCatalog,
  deleteCatalog: typeof deleteCatalog,
  getCatalogsAction: typeof getCatalogsAction,
  getCatalogTypesAction: typeof getCatalogTypesAction,
  history: RouterHistory,
  intl: IntlShape,
  isFullAdmin: boolean,
  location: Location,
  showDialogPanel: typeof showDialogPanel,
};

class CatalogsPage extends Component<Props> {
  handleClickAdd: Function;
  handleClickRemove: Function;

  constructor() {
    super();
    this.handleClickAdd = this.handleClickAdd.bind(this);
    this.handleClickRemove = this.handleClickRemove.bind(this);
  }

  componentDidMount() {
    this.props.getCatalogTypesAction();
    this.props.getCatalogsAction();
  }

  handleClickAdd() {
    this.props.showDialogPanel(CREATE_CATALOG_PANEL_ID);
  }

  handleClickRemove() {
    const { catalogs, deleteCatalog } = this.props;
    const selectedCatalogId = this.selectedCatalogId();
    const catalog =
      selectedCatalogId &&
      catalogs.find(c => c.catalogId === selectedCatalogId);
    catalog && deleteCatalog(catalog);
  }

  selectedCatalogId() {
    const parts = this.props.location.pathname.split('/');
    const idx = parts.indexOf('catalogs');
    return parts[idx + 1];
  }

  selectedCatalogSubpage() {
    const parts = this.props.location.pathname.split('/');
    const idx = parts.indexOf('catalogs');
    return parts.slice(idx + 2).join('/');
  }

  renderHeader() {
    return (
      <header className="page-header" data-test-name="catalogs-page">
        <FormattedMessage {...localizedStrings.NAV_CATALOGS} />
        {this.props.isFullAdmin && (
          <p className="buttons">
            <a
              className="button is-white is-outlined"
              onClick={this.handleClickAdd}
              data-test-name="add-catalog"
            >
              <span className="icon is-large">
                <i className="fa fa-lg fa-plus-circle" />
              </span>
            </a>
            <a
              className="button is-white is-outlined"
              disabled={!this.selectedCatalogId()}
              onClick={this.handleClickRemove}
              data-test-name="del-catalog"
            >
              <span className="icon">
                <i className="fa fa-lg fa-minus-circle" />
              </span>
            </a>
          </p>
        )}
      </header>
    );
  }

  render() {
    const { activePanelId, catalogs } = this.props;
    return (
      <div className="page-contents">
        {activePanelId === CREATE_CATALOG_PANEL_ID && <CreateCatalogDialog />}
        {activePanelId === DELETE_VAULT_CATALOG_PANEL_ID && (
          <DeleteVaultCatalogDialog />
        )}
        <div className="page-contents columns is-gapless">
          <div className="column is-one-quarter page">
            {this.renderHeader()}
            <div className="page bordered-page-column is-scrollable">
              <table className="table is-fullwidth is-hoverable">
                <tbody>
                  {catalogs.map(catalog => (
                    <CatalogNavLink
                      catalog={catalog}
                      key={catalog.catalogId}
                      subPage={this.selectedCatalogSubpage()}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {catalogs.length === 0 && (
            <div className="column is-three-quarters">
              <div className="page-header" />
              <div className="full-page-message">
                <FormattedMessage {...localizedStrings.NO_CATALOGS_EXIST} />
              </div>
            </div>
          )}
          {catalogs.map(catalog => (
            <Route
              key={catalog.catalogId}
              render={props => <CatalogRoute catalog={catalog} {...props} />}
            />
          ))}
          <Switch>
            {catalogs.length && (
              <Redirect
                exact
                from="/catalogs"
                to={`/catalogs/${catalogs[0].catalogId}/${
                  catalogPages[0].subroute
                }`}
              />
            )}
            <Redirect
              exact
              from="/catalogs/:catalogId"
              to={`/catalogs/:catalogId/${catalogPages[0].subroute}`}
            />
          </Switch>
        </div>
      </div>
    );
  }
}

let catalogPageWrapper = connect(
  state => ({
    activePanelId: getActivePanelId(state),
    isFullAdmin: getIsFullAdmin(state),
    catalogs: getCatalogs(state),
  }),
  (dispatch: Dispatch) =>
    bindActionCreatorsSafe({
      deleteCatalog,
      getCatalogsAction,
      getCatalogTypesAction,
      showDialogPanel,
    })
)(CatalogsPage);

export default withRouter(catalogPageWrapper);



// WEBPACK FOOTER //
// ./src/view/Pages/CatalogsPage/CatalogsPage.js